import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { X } from "lucide-react";

interface ImageItem {
  src: string;
  alt: {
    pl: string;
    en: string;
    ru: string;
    uk: string;
  };
}

const GalleryPage = () => {
  const { t, language } = useLanguage();
  const [lightboxImage, setLightboxImage] = useState<ImageItem | null>(null);

  const images: ImageItem[] = [
    {
      src: "/lovable-uploads/df481845-4045-41c1-9dd3-80b32c49e6f6.png",
      alt: {
        pl: "Sam Childers przemawiający",
        en: "Sam Childers speaking",
        ru: "Сэм Чайлдерс выступает",
        uk: "Сем Чайлдерс виступає",
      },
    },
    {
      src: "/lovable-uploads/cfd49810-9e13-4c83-ac6e-6b1eafbcfa71.png",
      alt: {
        pl: "Dzieci przy pompie wodnej",
        en: "Children at water pump",
        ru: "Дети у водяного насоса",
        uk: "Діти біля водяного насоса",
      },
    },
    {
      src: "/lovable-uploads/56913f09-fb42-4df9-8bcb-80b1e6405d2e.png",
      alt: {
        pl: "Sam na motocyklu",
        en: "Sam on motorcycle",
        ru: "Сэм на мотоцикле",
        uk: "Сем на мотоциклі",
      },
    },
    {
      src: "/lovable-uploads/c5680540-6f46-4041-9b8f-129e5b82289d.png",
      alt: {
        pl: "Portret afrykańskiego dziecka",
        en: "African child portrait",
        ru: "Портрет африканского ребенка",
        uk: "Портрет африканської дитини",
      },
    },
    {
      src: "/lovable-uploads/2aeccf39-c2f4-4067-b36f-01c22b30f90a.png",
      alt: {
        pl: "Dzieci w klasie",
        en: "Children in classroom",
        ru: "Дети в классе",
        uk: "Діти в класі",
      },
    },
    {
      src: "/lovable-uploads/a21377fa-8dad-4704-bed7-0540e2a448b9.png",
      alt: {
        pl: "Sam z dzieckiem",
        en: "Sam with child",
        ru: "Сэм с ребенком",
        uk: "Сем з дитиною",
      },
    },
    {
      src: "/lovable-uploads/3d96a73f-1b69-442f-bffc-fb7b6cce50da.png",
      alt: {
        pl: "Sam z małym chłopcem",
        en: "Sam with young boy",
        ru: "Сэм с маленьким мальчиком",
        uk: "Сем з маленьким хлопчиком",
      },
    },
    {
      src: "/lovable-uploads/6c8f0efb-816b-492f-8627-59724eac2d01.png",
      alt: {
        pl: "Sam z dziećmi",
        en: "Sam with children",
        ru: "Сэм с детьми",
        uk: "Сем з дітьми",
      },
    },
    {
      src: "/lovable-uploads/a0ddfa3b-980e-4114-974b-b59b1d3bcb38.png",
      alt: {
        pl: "Sam z nastolatkiem",
        en: "Sam with teenager",
        ru: "Сэм с подростком",
        uk: "Сем з підлітком",
      },
    },
    {
      src: "/lovable-uploads/8b2261a5-a18a-4501-a789-fecfaabf9959.png",
      alt: {
        pl: "Sam z małą dziewczynką",
        en: "Sam with young girl",
        ru: "Сэм с маленькой девочкой",
        uk: "Сем з маленькою дівчинкою",
      },
    },
    {
      src: "/lovable-uploads/a828384b-36a6-4a35-970b-450cece75960.png",
      alt: {
        pl: "Sam gotujący z kobietą",
        en: "Sam cooking with woman",
        ru: "Сэм готовит с женщиной",
        uk: "Сем готує з жінкою",
      },
    },
    {
      src: "/lovable-uploads/1a394c9f-c9e2-4d00-87ca-f1aca76c5f6d.png",
      alt: {
        pl: "Sam z grupą dzieci",
        en: "Sam with children group",
        ru: "Сэм с группой детей",
        uk: "Сем з групою дітей",
      },
    },
    {
      src: "/lovable-uploads/57bfaf37-3ef0-46a6-8cc1-30df3c45b852.png",
      alt: {
        pl: "Sam z chłopcem w obozie",
        en: "Sam with boy in camp",
        ru: "Сэм с мальчиком в лагере",
        uk: "Сем з хлопчиком у таборі",
      },
    },
    {
      src: "/lovable-uploads/9347c3aa-aef1-4f5a-b243-5ed2c388ee77.png",
      alt: {
        pl: "Sam z żołnierzami",
        en: "Sam with soldiers",
        ru: "Сэм с солдатами",
        uk: "Сем з солдатами",
      },
    },
    {
      src: "/lovable-uploads/d5cb72c1-d14e-4990-8aff-14bbce088ec2.png",
      alt: {
        pl: "Personel wojskowy",
        en: "Military personnel",
        ru: "Военный персонал",
        uk: "Військовий персонал",
      },
    },
  ];

  const openLightbox = (image: ImageItem) => {
    setLightboxImage(image);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = "auto";
  };

  return (
    <div className="pt-16">
      <div className="bg-secondary">
        <div className="container mx-auto py-16 px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t("nav.gallery")}
          </h1>
          <p className="text-xl text-muted-foreground">
            {t("gallery.description")}
          </p>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-lg shadow-md cursor-pointer transition-transform hover:scale-105"
              onClick={() => openLightbox(image)}
            >
              <img
                src={image.src}
                alt={image.alt[language]}
                className="w-full h-64 object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-primary p-2"
            onClick={closeLightbox}
            aria-label={t("gallery.close")}
          >
            <X size={24} />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt[language]}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
